import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Badge } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';

/**
 * Modal para crear/editar sub-estados
 * Maneja la validación y envío de datos para operaciones CRUD de sub-estados
 */
const SubEstadoModal = ({
  show,
  onHide,
  subEstado = null, // null para crear, objeto para editar
  estadoParent = null, // estado al que pertenece el sub-estado
  estadosForSelect = [], // lista de estados disponibles para select
  tipoEstadoNombre = '', // nombre del tipo de estado actual
  canAdmin = false,
  onSave,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    sub_estado: '',
    descripcion: '',
    estadoId: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!subEstado;
  const modalTitle = isEditing ? 'Editar Sub Estado' : 'Crear Nuevo Sub Estado';

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (show) {
      if (isEditing && subEstado) {
        // Modo edición: cargar datos del sub-estado
        setFormData({
          sub_estado: subEstado.sub_estado || '',
          descripcion: subEstado.descripcion || '',
          estadoId: subEstado.estadoParentId || ''
        });
      } else if (estadoParent) {
        // Modo creación con estado preseleccionado
        setFormData({
          sub_estado: '',
          descripcion: '',
          estadoId: estadoParent.id
        });
      } else {
        // Modo creación sin preselección
        setFormData({
          sub_estado: '',
          descripcion: '',
          estadoId: ''
        });
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [show, isEditing, subEstado, estadoParent]);

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.sub_estado.trim()) {
      newErrors.sub_estado = 'El nombre del sub-estado es requerido';
    } else if (formData.sub_estado.trim().length < 2) {
      newErrors.sub_estado = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    } else if (formData.descripcion.trim().length < 5) {
      newErrors.descripcion = 'La descripción debe tener al menos 5 caracteres';
    }

    if (!formData.estadoId) {
      newErrors.estadoId = 'Debe seleccionar un estado padre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        sub_estado: formData.sub_estado.trim(),
        descripcion: formData.descripcion.trim(),
        estadoId: parseInt(formData.estadoId)
      };

      let result;
      if (isEditing) {
        result = await onSave(subEstado.id, dataToSend);
      } else {
        result = await onSave(dataToSend);
      }

      if (result.success) {
        onHide(); // Cerrar modal solo si fue exitoso
      } else {
        // Mostrar errores específicos si los hay
        if (result.errors) {
          setErrors(result.errors);
        }
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Obtener información del estado seleccionado
  const selectedEstado = estadosForSelect.find(estado => estado.value === parseInt(formData.estadoId));

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title style={{ display: 'flex', alignItems: 'center', color: '#495057' }}>
          {isEditing ? (
            <FaIcons.FaEdit style={{ marginRight: '12px', color: '#6c757d' }} />
          ) : (
            <FaIcons.FaPlus style={{ marginRight: '12px', color: '#6c757d' }} />
          )}
          {modalTitle}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        {/* Mejor UI para tipo de estado */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: '#f4f8fb',
          borderRadius: 8,
          padding: '12px 20px 4px 20px',
          marginBottom: 0,
          border: '1px solid #e3e8ee',
          fontSize: '1.15rem',
          fontWeight: 600,
          color: '#495057',
          gap: 12
        }}>
          <FaIcons.FaLayerGroup style={{ fontSize: '1.3rem', color: '#17a2b8' }} />
          <span style={{ marginRight: 8 }}>Tipo de Estado:</span>
          <Badge bg="info" style={{ fontSize: '1.1rem', padding: '7px 18px', borderRadius: 6, letterSpacing: 1 }}>{tipoEstadoNombre}</Badge>
        </div>
        <Modal.Body style={{ padding: '24px' }}>
          {/* Información del contexto */}
          {(estadoParent || selectedEstado) && (
            <Alert variant="info" style={{ marginBottom: '20px' }}>
              <FaIcons.FaInfoCircle style={{ marginRight: '8px' }} />
              <strong>Contexto:</strong> Este sub-estado pertenecerá a{' '}
              <Badge bg="primary">
                {estadoParent ? 
                  `${estadoParent.estado} (${estadoParent.id})` : 
                  selectedEstado?.label
                }
              </Badge>
            </Alert>
          )}

          <Row>
            {/* Selección del estado padre */}
            <Col md={12} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  <FaIcons.FaList style={{ marginRight: '8px' }} />
                  Estado Padre *
                </Form.Label>
                <Form.Select
                  value={formData.estadoId}
                  onChange={(e) => handleInputChange('estadoId', e.target.value)}
                  isInvalid={!!errors.estadoId}
                  disabled={isEditing || !!estadoParent} // Deshabilitar en edición o si viene preseleccionado
                >
                  <option value="">Seleccionar estado...</option>
                  {estadosForSelect.map((estado) => (
                    <option key={estado.value} value={estado.value}>
                      {estado.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.estadoId}
                </Form.Control.Feedback>
                {(isEditing || estadoParent) && (
                  <Form.Text className="text-muted">
                    <FaIcons.FaLock style={{ marginRight: '4px' }} />
                    El estado padre no se puede cambiar
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Nombre del sub-estado */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  <FaIcons.FaTag style={{ marginRight: '8px' }} />
                  Nombre del Sub Estado *
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ej: Recepcionado, En Proceso, Finalizado..."
                  value={formData.sub_estado}
                  onChange={(e) => handleInputChange('sub_estado', e.target.value)}
                  isInvalid={!!errors.sub_estado}
                  maxLength={100}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sub_estado}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Máximo 100 caracteres
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Descripción */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  <FaIcons.FaAlignLeft style={{ marginRight: '8px' }} />
                  Descripción *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Describe brevemente el propósito de este sub-estado..."
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  isInvalid={!!errors.descripcion}
                  maxLength={255}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.descripcion}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Máximo 255 caracteres ({formData.descripcion.length}/255)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Preview del sub-estado */}
          {formData.sub_estado && formData.descripcion && selectedEstado && (
            <Alert variant="light" style={{ marginTop: '20px', border: '2px dashed #dee2e6' }}>
              <h6 style={{ color: '#495057', marginBottom: '8px' }}>
                <FaIcons.FaEye style={{ marginRight: '8px' }} />
                Vista Previa:
              </h6>
              <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '4px' }}>
                  {selectedEstado.label}
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#495057', marginBottom: '4px' }}>
                  <FaIcons.FaArrowRight style={{ marginRight: '8px', fontSize: '0.9rem', color: '#6c757d' }} />
                  {formData.sub_estado}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  {formData.descripcion}
                </div>
              </div>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
          <Button 
            variant="secondary" 
            onClick={onHide}
            disabled={isSubmitting || loading}
          >
            <FaIcons.FaTimes style={{ marginRight: '8px' }} />
            Cancelar
          </Button>
          
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting || loading}
          >
            {(isSubmitting || loading) ? (
              <>
                <FaIcons.FaSpinner className="fa-spin" style={{ marginRight: '8px' }} />
                {isEditing ? 'Actualizando...' : 'Creando...'}
              </>
            ) : (
              <>
                {isEditing ? (
                  <FaIcons.FaEdit style={{ marginRight: '8px' }} />
                ) : (
                  <FaIcons.FaPlus style={{ marginRight: '8px' }} />
                )}
                {isEditing ? 'Actualizar Sub Estado' : 'Crear Sub Estado'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubEstadoModal;
